import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  User,
  ArrowLeft,
  Calendar,
  MapPin,
  Target,
  Users,
  Clock,
  TrendingUp,
  Phone,
  Mail,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react'
import {
  getMemberReport,
  getMemberRoute,
  getClients,
  getMeetings,
  getTargets
} from '../../services/fieldTeamApi'
import './MemberDetail.css'

export default function MemberDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const memberId = parseInt(id)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Data states
  const [memberData, setMemberData] = useState(null)
  const [routeData, setRouteData] = useState(null)
  const [clients, setClients] = useState([])
  const [meetings, setMeetings] = useState([])
  const [targets, setTargets] = useState([])

  // Filter states
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [activeTab, setActiveTab] = useState('overview') // overview, meetings, clients, targets

  // Fetch member data
  useEffect(() => {
    fetchMemberData()
  }, [memberId])

  // Fetch route data when date changes
  useEffect(() => {
    fetchRouteData()
  }, [memberId, selectedDate])

  const fetchMemberData = async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch member report
      const reportRes = await getMemberReport(memberId)
      if (reportRes.data.success) {
        setMemberData(reportRes.data.data)
      }

      // Fetch clients
      const clientsRes = await getClients({ member_id: memberId, limit: 50 })
      if (clientsRes.data.success) {
        setClients(clientsRes.data.data)
      }

      // Fetch meetings
      const meetingsRes = await getMeetings({ member_id: memberId, limit: 50 })
      if (meetingsRes.data.success) {
        setMeetings(meetingsRes.data.data)
      }

      // Fetch targets
      const targetsRes = await getTargets({ member_id: memberId, active_only: false, limit: 10 })
      if (targetsRes.data.success) {
        setTargets(targetsRes.data.data)
      }
    } catch (err) {
      console.error('Member data fetch error:', err)
      setError('Failed to load member data')
    } finally {
      setLoading(false)
    }
  }

  const fetchRouteData = async () => {
    try {
      const res = await getMemberRoute(memberId, selectedDate)
      if (res.data.success) {
        setRouteData(res.data.data)
      }
    } catch (err) {
      console.error('Route fetch error:', err)
    }
  }

  const navigateDate = (direction) => {
    const current = new Date(selectedDate)
    current.setDate(current.getDate() + direction)
    setSelectedDate(current.toISOString().split('T')[0])
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getStatusIcon = (target) => {
    const progress = calculateTargetProgress(target)
    if (progress >= 100) return <CheckCircle size={16} className="status-achieved" />
    if (progress >= 70) return <AlertCircle size={16} className="status-warning" />
    return <XCircle size={16} className="status-behind" />
  }

  const calculateTargetProgress = (target) => {
    if (!target.target_meetings || target.target_meetings === 0) return 100
    const actual = target.actual_meetings || 0
    return Math.round((actual / target.target_meetings) * 100)
  }

  if (loading) {
    return (
      <div className="member-detail-page">
        <div className="loading-container">
          <RefreshCw size={24} className="spin" />
          <span>Loading member data...</span>
        </div>
      </div>
    )
  }

  if (error || !memberData) {
    return (
      <div className="member-detail-page">
        <div className="error-container">
          <span>{error || 'Member not found'}</span>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const { member, totals, daily_activity, recent_meetings } = memberData

  return (
    <div className="member-detail-page">
      {/* Header */}
      <div className="member-detail-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <div className="member-profile">
          <div className="member-avatar-large">
            <User size={32} />
          </div>
          <div className="member-info-header">
            <h1>{member.name}</h1>
            <span className="member-username">@{member.username}</span>
            <div className={`member-badge ${member.is_active ? 'active' : 'inactive'}`}>
              {member.is_active ? 'Active' : 'Inactive'}
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={fetchMemberData}>
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="member-stats-grid">
        <div className="member-stat-card">
          <div className="stat-icon-box meetings">
            <Calendar size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{totals.meetings}</span>
            <span className="stat-label">Meetings (30d)</span>
          </div>
        </div>
        <div className="member-stat-card">
          <div className="stat-icon-box clients">
            <Users size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{totals.clients}</span>
            <span className="stat-label">Clients (30d)</span>
          </div>
        </div>
        <div className="member-stat-card">
          <div className="stat-icon-box trials">
            <TrendingUp size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{totals.trials}</span>
            <span className="stat-label">Trials (30d)</span>
          </div>
        </div>
        <div className="member-stat-card">
          <div className="stat-icon-box sales">
            <Target size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{totals.sales}</span>
            <span className="stat-label">Sales (30d)</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="member-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'meetings' ? 'active' : ''}`}
          onClick={() => setActiveTab('meetings')}
        >
          Meetings ({meetings.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'clients' ? 'active' : ''}`}
          onClick={() => setActiveTab('clients')}
        >
          Clients ({clients.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'targets' ? 'active' : ''}`}
          onClick={() => setActiveTab('targets')}
        >
          Targets ({targets.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <OverviewTab
            member={member}
            dailyActivity={daily_activity}
            recentMeetings={recent_meetings}
            routeData={routeData}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onNavigateDate={navigateDate}
          />
        )}

        {activeTab === 'meetings' && (
          <MeetingsTab meetings={meetings} />
        )}

        {activeTab === 'clients' && (
          <ClientsTab clients={clients} />
        )}

        {activeTab === 'targets' && (
          <TargetsTab targets={targets} />
        )}
      </div>
    </div>
  )
}

// Overview Tab Component
function OverviewTab({ member, dailyActivity, recentMeetings, routeData, selectedDate, onDateChange, onNavigateDate }) {
  const maxMeetings = Math.max(...dailyActivity.map(d => d.meetings), 1)

  return (
    <div className="overview-tab">
      {/* Activity Chart */}
      <div className="overview-section">
        <h3>Activity (Last 30 Days)</h3>
        <div className="activity-chart-container">
          {dailyActivity.length > 0 ? (
            <div className="activity-bars">
              {dailyActivity.map(day => (
                <div
                  key={day.date}
                  className="activity-bar"
                  style={{ height: `${Math.max((day.meetings / maxMeetings) * 100, 8)}%` }}
                  title={`${day.date}: ${day.meetings} meetings`}
                >
                  <span className="bar-tooltip">{day.meetings}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-chart">No activity data available</div>
          )}
        </div>
      </div>

      {/* Route Map Preview */}
      <div className="overview-section">
        <div className="section-header">
          <h3>Route for {formatDateReadable(selectedDate)}</h3>
          <div className="date-nav-small">
            <button onClick={() => onNavigateDate(-1)}>
              <ChevronLeft size={16} />
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => onDateChange(e.target.value)}
            />
            <button onClick={() => onNavigateDate(1)}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <div className="route-preview">
          {routeData && routeData.locations && routeData.locations.length > 0 ? (
            <div className="route-stats">
              <div className="route-stat">
                <MapPin size={16} />
                <span>{routeData.locations.length} location pings</span>
              </div>
              <div className="route-stat">
                <Clock size={16} />
                <span>{routeData.stats?.active_hours || 0}h active</span>
              </div>
              <div className="route-stat">
                <TrendingUp size={16} />
                <span>{routeData.stats?.distance_km || 0} km traveled</span>
              </div>
            </div>
          ) : (
            <div className="no-route">No route data for this date</div>
          )}
        </div>
      </div>

      {/* Recent Meetings */}
      <div className="overview-section">
        <h3>Recent Meetings</h3>
        {recentMeetings && recentMeetings.length > 0 ? (
          <div className="recent-list">
            {recentMeetings.slice(0, 5).map(meeting => (
              <div key={meeting.id} className="recent-item">
                <div className="recent-icon">
                  <Calendar size={14} />
                </div>
                <div className="recent-info">
                  <span className="recent-title">{meeting.client_name || 'Unknown Client'}</span>
                  <span className="recent-date">{formatDateReadable(meeting.meeting_date)}</span>
                </div>
                <span className={`interest-badge ${meeting.interest_level}`}>
                  {meeting.interest_level || 'N/A'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-list">No recent meetings</div>
        )}
      </div>

      {/* Member Info */}
      <div className="overview-section">
        <h3>Member Information</h3>
        <div className="member-info-grid">
          <div className="info-item">
            <span className="info-label">Joined</span>
            <span className="info-value">{formatDateReadable(member.joined_at)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Status</span>
            <span className={`info-value status-${member.is_active ? 'active' : 'inactive'}`}>
              {member.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Meetings Tab Component
function MeetingsTab({ meetings }) {
  return (
    <div className="meetings-tab">
      {meetings.length > 0 ? (
        <table className="detail-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Client</th>
              <th>Type</th>
              <th>Interest</th>
              <th>Outcome</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {meetings.map(meeting => (
              <tr key={meeting.id}>
                <td>{formatDateReadable(meeting.meeting_date)}</td>
                <td>{meeting.client_name || meeting.location_address || 'Unknown'}</td>
                <td className="capitalize">{meeting.meeting_type || '-'}</td>
                <td>
                  <span className={`interest-badge ${meeting.interest_level}`}>
                    {meeting.interest_level || '-'}
                  </span>
                </td>
                <td className="capitalize">{meeting.outcome || '-'}</td>
                <td>
                  <span className={`status-badge ${meeting.status}`}>
                    {meeting.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="empty-tab">
          <Calendar size={48} />
          <h4>No Meetings</h4>
          <p>This member hasn't logged any meetings yet.</p>
        </div>
      )}
    </div>
  )
}

// Clients Tab Component
function ClientsTab({ clients }) {
  return (
    <div className="clients-tab">
      {clients.length > 0 ? (
        <table className="detail-table">
          <thead>
            <tr>
              <th>Business Name</th>
              <th>Contact</th>
              <th>Phone</th>
              <th>Industry</th>
              <th>Status</th>
              <th>Added</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(client => (
              <tr key={client.id}>
                <td className="client-name">{client.business_name}</td>
                <td>{client.contact_name || '-'}</td>
                <td>
                  {client.contact_phone ? (
                    <a href={`tel:${client.contact_phone}`} className="phone-link">
                      <Phone size={12} />
                      {client.contact_phone}
                    </a>
                  ) : '-'}
                </td>
                <td className="capitalize">{client.industry || '-'}</td>
                <td>
                  <span className={`status-badge ${client.status}`}>
                    {client.status}
                  </span>
                </td>
                <td>{formatDateReadable(client.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="empty-tab">
          <Users size={48} />
          <h4>No Clients</h4>
          <p>This member hasn't added any clients yet.</p>
        </div>
      )}
    </div>
  )
}

// Targets Tab Component
function TargetsTab({ targets }) {
  const calculateProgress = (target) => {
    const metrics = [
      { name: 'Meetings', actual: target.actual_meetings || 0, target: target.target_meetings },
      { name: 'Clients', actual: target.actual_new_clients || 0, target: target.target_new_clients },
      { name: 'Trials', actual: target.actual_trials || 0, target: target.target_trials },
      { name: 'Sales', actual: target.actual_sales || 0, target: target.target_sales }
    ]
    return metrics.filter(m => m.target > 0)
  }

  const isActive = (target) => {
    const today = new Date().toISOString().split('T')[0]
    return target.period_start <= today && target.period_end >= today
  }

  return (
    <div className="targets-tab">
      {targets.length > 0 ? (
        <div className="targets-grid">
          {targets.map(target => {
            const progress = calculateProgress(target)
            const active = isActive(target)

            return (
              <div key={target.id} className={`target-card ${active ? 'active' : 'past'}`}>
                <div className="target-header">
                  <span className="target-type">{target.period_type}</span>
                  <span className={`target-status ${active ? 'active' : 'completed'}`}>
                    {active ? 'Active' : 'Completed'}
                  </span>
                </div>
                <div className="target-period">
                  {formatDateReadable(target.period_start)} - {formatDateReadable(target.period_end)}
                </div>
                <div className="target-metrics">
                  {progress.map(metric => {
                    const pct = Math.min(Math.round((metric.actual / metric.target) * 100), 100)
                    return (
                      <div key={metric.name} className="metric-row">
                        <div className="metric-label">
                          <span>{metric.name}</span>
                          <span>{metric.actual} / {metric.target}</span>
                        </div>
                        <div className="metric-bar">
                          <div
                            className={`metric-fill ${pct >= 100 ? 'complete' : pct >= 70 ? 'good' : 'behind'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="empty-tab">
          <Target size={48} />
          <h4>No Targets</h4>
          <p>No targets have been set for this member.</p>
        </div>
      )}
    </div>
  )
}

// Helper function
function formatDateReadable(dateStr) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}
